import { useRef, useEffect, useState, type KeyboardEvent } from 'react'
import dayjs from 'dayjs'
import { useHistory, useLocation } from 'react-router-dom'
import { Text, SearchInput, Flex, spacing } from '@retailer-platform/shared-components/src/tds'
import { useDebouncedState, colors } from '@retailer-platform/shared-components'
import { UserIcon, CartIcon, BagIcon } from '@instacart/ids-core'
import { useTrackEvent } from '@retailer-platform/dashboard/analytics'
import { useDomainIntl, useDomainMessages } from '../utils/domain/intl'
import { useFindUsers } from '../utils/hooks/useFindUsers.hooks'
import { useGetOrderByIdBasic } from '../utils/hooks/useGetOrderByIdBasic.hooks'
import { useSearchShoppers } from '../utils/hooks/useSearchShoppers.hooks'
import { useSearchApplicants } from '../utils/hooks/useSearchApplicants.hooks'
import { useRegionNames } from '../utils/hooks/useRegionNames.hooks'
import {
  buildCustomerUrl,
  buildOrderUrl,
  buildShopperUrl,
  buildApplicantUrl,
} from '../utils/domain/routing'
import { formatPhoneNumber } from '../utils/domain/phone'
import {
  trackFastFindCustomerNavigated,
  trackFastFindOrderNavigated,
  trackSearchBarCustomerNavigated,
  trackSearchBarOrderNavigated,
} from '../utils/domain/tracking'
import CategorizedResultsDropdown from './CategorizedResultsDropdown'
import StoreMarker from './StoreMarker'

export enum TrackingSurface {
  SEARCH_BAR,
  FAST_FIND,
}

interface CustomerOrderSearchInputProps {
  onSearch: (query: string) => void
  onNavigate?: () => void
  onQueryChange?: (query: string) => void
  placeholder?: string
  labelProps?: { label: string }
  initialValue?: string
  autoFocus?: boolean
  enterGoesToFirstResult?: boolean
  trackingSurface?: TrackingSurface
}

const CustomerOrderSearchInput = ({
  onSearch,
  onNavigate,
  onQueryChange,
  placeholder,
  labelProps,
  initialValue,
  autoFocus,
  enterGoesToFirstResult = false,
  trackingSurface,
}: CustomerOrderSearchInputProps) => {
  const [query, debouncedQuery, setQuery] = useDebouncedState(initialValue ?? '', 300)
  const history = useHistory()
  const location = useLocation()
  const trackEvent = useTrackEvent()

  const trackCustomerNavigate = (customerId: string) => {
    if (trackingSurface === TrackingSurface.FAST_FIND) {
      trackFastFindCustomerNavigated(trackEvent, {
        customerId,
        sourcePage: location.pathname,
      })
    } else if (trackingSurface === TrackingSurface.SEARCH_BAR) {
      trackSearchBarCustomerNavigated(trackEvent, { customerId })
    }
  }

  const trackOrderNavigate = (customerId: string, orderId: string) => {
    if (trackingSurface === TrackingSurface.FAST_FIND) {
      trackFastFindOrderNavigated(trackEvent, {
        customerId,
        orderId,
        sourcePage: location.pathname,
      })
    } else if (trackingSurface === TrackingSurface.SEARCH_BAR) {
      trackSearchBarOrderNavigated(trackEvent, { customerId, orderId })
    }
  }

  const intl = useDomainIntl()
  const messages = useDomainMessages({
    searchResultsHeader: 'customersAndOrdersDomain.admin.customersAndOrders.searchResultsHeader',
    searchNoResults: 'customersAndOrdersDomain.admin.customersAndOrders.searchNoResults',
    ordersResultsHeader: 'customersAndOrdersDomain.admin.customersAndOrders.ordersResultsHeader',
    ordersNoResults: 'customersAndOrdersDomain.admin.customersAndOrders.ordersNoResults',
    orderLabel: 'customersAndOrdersDomain.admin.customersAndOrders.orderLabel',
    orderArriving: 'customersAndOrdersDomain.admin.customersAndOrders.orderArriving',
    shoppersResultsHeader:
      'customersAndOrdersDomain.admin.customersAndOrders.shoppersResultsHeader',
    shoppersNoResults: 'customersAndOrdersDomain.admin.customersAndOrders.shoppersNoResults',
    applicantsResultsHeader:
      'customersAndOrdersDomain.admin.customersAndOrders.applicantsResultsHeader',
    applicantsNoResults: 'customersAndOrdersDomain.admin.customersAndOrders.applicantsNoResults',
    customerId: 'customersAndOrdersDomain.admin.customersAndOrders.customerCard.id',
    customerEmail: 'customersAndOrdersDomain.admin.customersAndOrders.customerCard.email',
    customerPhone: 'customersAndOrdersDomain.admin.customersAndOrders.customerCard.phone',
    customerLastOrder: 'customersAndOrdersDomain.admin.customersAndOrders.customerCard.lastOrder',
  })

  const numericQuery = /^\d+$/.test(debouncedQuery.trim()) ? debouncedQuery.trim() : null
  const { data: users, loading: usersLoading } = useFindUsers(debouncedQuery)
  const { data: order, loading: orderLoading } = useGetOrderByIdBasic(numericQuery)
  const { data: shoppers, loading: shoppersLoading } = useSearchShoppers(debouncedQuery)
  const { data: applicants, loading: applicantsLoading } = useSearchApplicants(debouncedQuery)
  const regionNames = useRegionNames()

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [dropdownDismissed, setDropdownDismissed] = useState(false)

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  useEffect(() => {
    if (initialValue !== undefined) setQuery(initialValue)
  }, [initialValue, setQuery])

  useEffect(() => {
    setDropdownDismissed(false)
  }, [debouncedQuery])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownDismissed(true)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleChange = (value: string) => {
    setQuery(value)
    onQueryChange?.(value)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmed = query.trim()
      if (!trimmed) return
      if (enterGoesToFirstResult) {
        const firstUser = users?.[0]
        if (firstUser?.id) {
          const customerId = String(firstUser.id)
          trackCustomerNavigate(customerId)
          onNavigate?.()
          history.push(buildCustomerUrl(customerId))
        }
      } else {
        onSearch(trimmed)
      }
    }
  }

  const showDropdown = debouncedQuery.trim().length > 0 && !dropdownDismissed

  return (
    <div css={{ flex: 1, position: 'relative' }} ref={containerRef}>
      <SearchInput
        ref={inputRef}
        placeholder={placeholder}
        labelProps={labelProps}
        value={query}
        onChange={evt => handleChange(evt.target.value)}
        onFocus={() => setDropdownDismissed(false)}
        onKeyDown={handleKeyDown}
        onClear={() => handleChange('')}
        width="full"
      />
      {showDropdown && (
        <div
          css={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            maxWidth: '628px',
            marginTop: spacing.s4,
            zIndex: 700,
          }}
        >
          <CategorizedResultsDropdown
            loading={usersLoading || orderLoading}
            sections={[
              {
                title: messages.searchResultsHeader,
                icon: <UserIcon size={16} />,
                emptyLabel: messages.searchNoResults,
                items: (users ?? []).map(user => (
                  <Flex.Column
                    key={String(user?.id)}
                    onClick={() => {
                      const customerId = String(user?.id)
                      trackCustomerNavigate(customerId)
                      onNavigate?.()
                      history.push(buildCustomerUrl(customerId))
                    }}
                    css={{
                      padding: `${spacing.s8} ${spacing.s16}`,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
                      borderBottom: `1px solid ${colors.GRAYSCALE.X10}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Flex.Row gap={spacing.s8} css={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Text typography="bodyEmphasized" css={{ marginBottom: '0 !important' }}>
                        {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
                      </Text>
                      {user?.isolationContext?.name && (
                        <StoreMarker name={user.isolationContext.name} />
                      )}
                    </Flex.Row>
                    <Flex.Row
                      gap={spacing.s16}
                      css={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
                    >
                      {[
                        { label: messages.customerId, value: user?.id },
                        { label: messages.customerPhone, value: formatPhoneNumber(user?.phone) },
                        { label: messages.customerEmail, value: user?.email },
                        {
                          label: messages.customerLastOrder,
                          value: user?.lastOrderedAt
                            ? intl.formatDate(user.lastOrderedAt, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                              })
                            : null,
                        },
                      ]
                        .filter(({ value }) => Boolean(value))
                        .map(({ label, value }) => (
                          <Flex.Row
                            key={label}
                            gap={spacing.s4}
                            css={{
                              alignItems: 'baseline',
                              flexWrap: 'wrap',
                              minWidth: 0,
                              maxWidth: '100%',
                            }}
                          >
                            <Text typography="bodyRegular" color="systemGrayscale60">
                              {label}
                            </Text>
                            <Text
                              typography="bodyRegular"
                              css={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                            >
                              {value}
                            </Text>
                          </Flex.Row>
                        ))}
                    </Flex.Row>
                  </Flex.Column>
                )),
              },
              ...(numericQuery
                ? [
                    {
                      title: messages.ordersResultsHeader,
                      icon: <CartIcon size={16} />,
                      emptyLabel: messages.ordersNoResults,
                      items: order
                        ? [
                            <Flex.Column
                              key={order.id}
                              onClick={() => {
                                const customerId = String(order.userId)
                                const orderId = String(order.id)
                                trackOrderNavigate(customerId, orderId)
                                onNavigate?.()
                                history.push(
                                  buildOrderUrl(customerId, orderId, order.orderReference)
                                )
                              }}
                              css={{
                                padding: `${spacing.s8} ${spacing.s16}`,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
                              }}
                            >
                              <Text
                                typography="bodyEmphasized"
                                css={{ marginBottom: '0 !important' }}
                              >
                                {messages.orderLabel} {order.id}
                              </Text>
                              <Text typography="bodyRegular" color="systemGrayscale60">
                                {[
                                  order.userFullName,
                                  order.fulfillmentDate
                                    ? `${messages.orderArriving} ${dayjs(order.fulfillmentDate).format('M/DD/YYYY [by] ha')}`
                                    : null,
                                  order.serviceType
                                    ?.replace(/^SERVICE_TYPE_/, '')
                                    .toLowerCase()
                                    .replace(/^\w/, c => c.toUpperCase()),
                                ]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </Text>
                            </Flex.Column>,
                          ]
                        : [],
                    },
                  ]
                : []),
              {
                title: messages.shoppersResultsHeader,
                icon: <BagIcon size={16} />,
                emptyLabel: messages.shoppersNoResults,
                loading: shoppersLoading,
                items: (shoppers ?? []).map(shopper => (
                  <Flex.Column
                    key={String(shopper.id)}
                    onClick={() => {
                      onNavigate?.()
                      window.open(buildShopperUrl(String(shopper.id)), '_blank')
                    }}
                    css={{
                      padding: `${spacing.s8} ${spacing.s16}`,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
                      borderBottom: `1px solid ${colors.GRAYSCALE.X10}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Text typography="bodyEmphasized" css={{ marginBottom: '0 !important' }}>
                      {shopper.name}
                    </Text>
                    <Text
                      typography="bodyRegular"
                      color="systemGrayscale60"
                      css={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                    >
                      {[
                        shopper.id,
                        shopper.email,
                        regionNames && shopper.regionId != null
                          ? (regionNames.get(String(shopper.regionId)) ??
                            `Region ${shopper.regionId}`)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Text>
                  </Flex.Column>
                )),
              },
              {
                title: messages.applicantsResultsHeader,
                icon: <UserIcon size={16} />,
                emptyLabel: messages.applicantsNoResults,
                loading: applicantsLoading,
                items: (applicants ?? []).map(applicant => (
                  <Flex.Column
                    key={String(applicant.id)}
                    onClick={() => {
                      onNavigate?.()
                      window.open(buildApplicantUrl(String(applicant.id)), '_blank')
                    }}
                    css={{
                      padding: `${spacing.s8} ${spacing.s16}`,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
                      borderBottom: `1px solid ${colors.GRAYSCALE.X10}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Text typography="bodyEmphasized" css={{ marginBottom: '0 !important' }}>
                      {applicant.name}
                    </Text>
                    <Text
                      typography="bodyRegular"
                      color="systemGrayscale60"
                      css={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                    >
                      {[
                        applicant.id,
                        applicant.email,
                        regionNames && applicant.regionId != null
                          ? (regionNames.get(String(applicant.regionId)) ??
                            `Region ${applicant.regionId}`)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Text>
                  </Flex.Column>
                )),
              },
            ]}
          />
        </div>
      )}
    </div>
  )
}

export default CustomerOrderSearchInput
